/**
 * Auto-start gates for the first-login walkthrough.
 *
 * The product tour (z-120) and the welcome splash (z-60) both stack ABOVE the
 * web weekly-MFA / soft-lock sheet (z-50). If either overlay starts while a
 * password/OTP challenge is still open, the user cannot type the code and
 * cannot finish signing in. Auto-start must wait until auth is fully settled
 * and no other modal is covering the shell; when a gate is closed, the caller
 * re-arms so the tour still appears after login.
 */

/** Marks walkthrough overlays so they are not treated as "blocking" themselves. */
export const ONBOARDING_TOUR_ATTR = 'data-onboarding-tour'

export type TourAutoStartGate = {
  isAuthLoading: boolean
  hasUser: boolean
  onBlockedRoute: boolean
  callActive: boolean
  /**
   * A session recording is capturing (or paused) on this device. Its REC bar
   * and stop controls must stay reachable, so the tour never covers them.
   */
  recordingActive?: boolean
  softLocked: boolean
  blockingModalOpen: boolean
  /**
   * Role / Independent follow summary still loading — the follower-only
   * Sessions step depends on it, so wait instead of starting without it.
   */
  followSummaryPending?: boolean
  /**
   * First-run setup is owed on this launch and not yet finished or dismissed.
   * The walkthrough runs after setup, so it waits until this turns false.
   */
  setupPending?: boolean
}

/** True when the walkthrough must not auto-start (retry when this becomes false). */
export function isTourAutoStartBlocked(gate: TourAutoStartGate): boolean {
  return (
    gate.isAuthLoading ||
    !gate.hasUser ||
    gate.onBlockedRoute ||
    gate.callActive ||
    gate.recordingActive === true ||
    gate.softLocked ||
    gate.blockingModalOpen ||
    gate.followSummaryPending === true ||
    gate.setupPending === true
  )
}

/**
 * True when some other modal dialog is open above the app shell.
 * Walkthrough overlays (tagged {@link ONBOARDING_TOUR_ATTR}) are ignored so
 * the tour does not treat itself as a reason to stay idle.
 */
export function hasBlockingModal(root: ParentNode | null | undefined = document): boolean {
  if (!root || typeof root.querySelectorAll !== 'function') return false
  const nodes = root.querySelectorAll('[role="dialog"][aria-modal="true"]')
  for (const node of nodes) {
    if (!(node instanceof Element)) continue
    if (node.closest(`[${ONBOARDING_TOUR_ATTR}]`)) continue
    return true
  }
  return false
}
